<template>
  <div class="users-list">
    <TotalInfoBlock v-if="paginationData?.total" :total="parseInt(paginationData?.total)" />
    <template v-if="isMobileDevice">
      <div v-if="items.length">
        <CustomersCard
          v-for="item of items"
          :id="item.id"
          :key="item.id"
          :status="item.status"
          :employee="item.user"
          :contacts="item.contacts"
          :visit="item.visit"
          :is-impersonate-available="true"
          :impersonate-is-user-dealer="false"
          impersonate-permission="employee.impersonate"
          impersonate-return-route="Users"
          @clickOnOrder="onClickRow({ id: item.id })"
        />
      </div>
      <SkeletonLoaderCard v-else :table-headers="resolvedTableHeaders" />
    </template>

    <OurTable
      v-else
      :headers="resolvedTableHeaders"
      :items="items"
      :table-fixed="false"
      class="users-list-table"
    >
      <template #cell-contacts="{ value }">
        <div class="contacts-block">
          <OurLink
            class="email"
            :text="value.email"
            :url="value.email"
            type="email"
            dashed
            size="sm"
            color="accent"
            no-focus-ring
            @click.native.stop
          />
        </div>
      </template>

      <template #cell-status="{ value }">
        <OurTag size="md" :text="value?.text" :color="value?.color" />
      </template>

      <template
        v-if="getPermission['employee.impersonate'] && !isAuthenticatedImpersonateUser"
        #cell-buttons="{ value }"
      >
        <OurLink no-focus-ring @click.native.stop="onClickHelperMenu(value)">
          <div class="user-row relative">
            <OurSvgIcon :src="icons.more" />

            <div v-if="isShownMenuBlock === value" class="menu-block">
              <a href="" @click.prevent="loginImpersonateHandler(value)">
                {{ $t("page.users.enter") }}
              </a>
            </div>
          </div>
        </OurLink>
      </template>
    </OurTable>

    <OurPagination
      v-if="isMoreThanOnePage"
      :current-page="paginationData.currentPage"
      :per-page="paginationData.perPage"
      :total="paginationData.total"
      :disabled="isLoading"
      @pageChange="onPageChange"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CustomersCard from "@/components/MobileCards/CustomersCard.vue";
import SkeletonLoaderCard from "@/ui/loader/SkeletonLoaderCard.vue";
import TotalInfoBlock from "@/components/TotalInfoBlock.vue";
import { buildEmployeesListTableHeaders } from "@/views/settings/users/index/config/employeesListTableHeaders";

export default {
  name: "EmployeesList",
  components: { CustomersCard, SkeletonLoaderCard, TotalInfoBlock },

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    paginationData: {
      type: Object,
      default: () => {},
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    headers: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      isShownMenuBlock: null,
    };
  },

  computed: {
    ...mapGetters(["isAuthenticatedImpersonateUser"]),
    ...mapGetters("breakpoint", ["isMobileDevice"]),
    ...mapGetters("user", ["getPermission", "userProfileName", "isUserDealer"]),
    icons: () => ({
      more: require("@material-symbols/svg-400/outlined/more_horiz-fill.svg"),
    }),
    showImpersonateColumn() {
      return (
        !this.isUserDealer &&
        this.getPermission["employee.impersonate"] &&
        !this.isAuthenticatedImpersonateUser
      );
    },

    resolvedTableHeaders() {
      if (this.headers) {
        return this.headers;
      }

      return buildEmployeesListTableHeaders({
        t: (key) => this.$t(key),
        showImpersonateColumn: this.showImpersonateColumn,
      });
    },

    isMoreThanOnePage() {
      return this.paginationData.lastPage > 1;
    },

    routePage() {
      return Number.parseInt(this.$route.query.page, 10) || 1;
    },
  },

  watch: {
    routePage(newPage) {
      if (newPage !== this.paginationData.currentPage) {
        this.$emit("pageChange", newPage);
      }
    },
  },

  created() {
    window.addEventListener("click", this.closeMenuBlock);
  },

  destroyed() {
    window.removeEventListener("click", this.closeMenuBlock);
  },

  methods: {
    ...mapActions(["loginImpersonate"]),

    async loginImpersonateHandler(val) {
      if (this.isAuthenticatedImpersonateUser) return;

      window.localStorage.setItem("name", this.userProfileName);
      window.localStorage.setItem("impersonateReturnRoute", "Users");
      await this.$router.push({ name: "Dashboard" });
      await this.loginImpersonate({ id: val, isUserDealer: false });
      this.$router.go();
    },

    onPageChange(page) {
      this.$emit("pageChange", page);

      if (page !== this.routePage) {
        const query = { ...this.$route.query };

        delete query.page;

        const nextQuery = page === 1 ? query : { ...query, page };

        this.$router.push({ name: this.$route.name, query: nextQuery });
      }
    },

    onClickRow(row) {
      this.$emit("onClickRow", row.id);
    },

    closeMenuBlock(event) {
      if (!this.$el.contains(event.target)) {
        this.isShownMenuBlock = false;
      }
    },

    onClickHelperMenu(val) {
      if (this.isShownMenuBlock === val) {
        this.isShownMenuBlock = null;

        return;
      }

      this.isShownMenuBlock = val;
    },
  },
};
</script>

<style lang="postcss" scoped>
.users-list {
  .contacts-block {
    @apply overflow-hidden;

    .email {
      @apply truncate;
    }
  }
}

.menu-block {
  @apply absolute right-0 top-6;
  @apply h-auto;
  @apply rounded-lg border border-gray-700 bg-gray-900 text-white;
  @apply py-1;
  @applu flex flex-col;
  a {
    @apply block whitespace-nowrap px-3 py-2;
    &:hover {
      @apply bg-gray-800;
    }
  }
}

.user-row {
  @apply flex h-8 w-8 items-center justify-center rounded transition-all hover:bg-background-light;
}

.user-row:last-child {
  .menu-block {
    top: -40%;
    right: 110%;
  }
}
</style>
